import { supabase } from "@/integrations/supabase/client";

export type OrgHeader = {
  id: string;
  name: string;
  logo_url: string | null;
  website: string | null;
  subscription_status: string | null;
  is_test_org: boolean | null;
  plan_id: string | null;
  plan_name: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  created_at: string | null;
  industry_name: string | null;
  industry_emoji: string | null;
};

export const superAdminOrgService = {
  async getOrgOverview(organizationId: string): Promise<{
    data: {
      id: string;
      created_at: string | null;
      current_period_end: string | null;
      onboarding_meta: any | null;
      active_agents_count: number | null;
      ai_assistant_instances_count: number | null;
      ai_outputs_count: number | null;
      files_count: number | null;
      knowledge_items_count: number | null;
      storage_usage_mb: number | null;
      team_members_count: number | null;
      conversations_this_month: number | null;
      ai_generations_this_month: number | null;
      api_calls_this_month: number | null;
      voice_calls_this_month: number | null;
      voice_minutes_used_this_month: number | null;
    } | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select(
          `
          id,
          created_at,
          current_period_end,
          onboarding_meta,
          active_agents_count,
          ai_assistant_instances_count,
          files_count,
          knowledge_items_count,
          storage_usage_mb,
          team_members_count,
          conversations_this_month,
          ai_generations_this_month,
          api_calls_this_month,
          voice_calls_this_month,
          voice_minutes_used_this_month
        `,
        )
        .eq("id", organizationId)
        .single();

      if (error) return { data: null, error: new Error(error.message) };

      // Default output count is null; try to get via RPC
      let aiOutputsCount: number | null = null;
      try {
        const { data: outputsAgg, error: outputsErr } = await supabase.rpc(
          "get_org_ai_outputs_counts",
          { org_ids: [organizationId] },
        );
        if (!outputsErr && Array.isArray(outputsAgg) && outputsAgg.length > 0) {
          const row = (outputsAgg as any[])[0];
          aiOutputsCount = row?.count != null ? Number(row.count) : null;
        }
      } catch (e) {
        // swallow; keep null if RPC not available
      }

      return {
        data: {
          id: data.id,
          created_at: (data as any).created_at ?? null,
          current_period_end: (data as any).current_period_end ?? null,
          onboarding_meta: (data as any).onboarding_meta ?? null,
          active_agents_count: (data as any).active_agents_count ?? null,
          ai_assistant_instances_count:
            (data as any).ai_assistant_instances_count ?? null,
          ai_outputs_count: aiOutputsCount,
          files_count: (data as any).files_count ?? null,
          knowledge_items_count: (data as any).knowledge_items_count ?? null,
          storage_usage_mb: (data as any).storage_usage_mb ?? null,
          team_members_count: (data as any).team_members_count ?? null,
          conversations_this_month:
            (data as any).conversations_this_month ?? null,
          ai_generations_this_month:
            (data as any).ai_generations_this_month ?? null,
          api_calls_this_month: (data as any).api_calls_this_month ?? null,
          voice_calls_this_month: (data as any).voice_calls_this_month ?? null,
          voice_minutes_used_this_month:
            (data as any).voice_minutes_used_this_month ?? null,
        },
        error: null,
      };
    } catch (e) {
      return {
        data: null,
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },
  async getOrgHeader(organizationId: string): Promise<{
    data: OrgHeader | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select(
          `
          id,
          name,
          logo_url,
          website,
          subscription_status,
          is_test_org,
          plan_id,
          stripe_customer_id,
          stripe_subscription_id,
          current_period_end,
          created_at,
          industries(name, emoji),
          plans(name)
        `,
        )
        .eq("id", organizationId)
        .single();

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      const header: OrgHeader = {
        id: data.id,
        name: data.name,
        logo_url: (data as any).logo_url ?? null,
        website: (data as any).website ?? null,
        subscription_status: (data as any).subscription_status ?? null,
        is_test_org: (data as any).is_test_org ?? null,
        plan_id: (data as any).plan_id ?? null,
        plan_name: (data as any).plans?.name ?? null,
        stripe_customer_id: (data as any).stripe_customer_id ?? null,
        stripe_subscription_id: (data as any).stripe_subscription_id ?? null,
        current_period_end: (data as any).current_period_end ?? null,
        created_at: (data as any).created_at ?? null,
        industry_name: (data as any).industries?.name ?? null,
        industry_emoji: (data as any).industries?.emoji ?? null,
      };

      return { data: header, error: null };
    } catch (e) {
      return {
        data: null,
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },

  async openBillingPortal(organizationId: string): Promise<{
    url?: string;
    error?: Error;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-billing-portal",
        {
          body: { organizationId },
        },
      );
      if (error) return { error: new Error(error.message) };
      if (!data?.url) return { error: new Error("No portal URL returned") };
      return { url: data.url };
    } catch (e) {
      return { error: e as Error };
    }
  },

  async getEffectivePlan(organizationId: string): Promise<{
    data: {
      organization_id: string;
      plan_id: string | null;
      plan_name: string | null;
      subscription_id: string | null;
      subscription_status: string | null;
      trial_end: number | null;
    } | null;
    error: Error | null;
  }> {
    try {
      // Attempt RPC if available
      try {
        const { data, error } = await supabase.rpc(
          "get_org_effective_plan" as any,
          { org_id: organizationId } as any,
        );
        if (!error && data) {
          const row = Array.isArray(data) ? (data[0] as any) : (data as any);
          if (row) {
            return {
              data: {
                organization_id: row.organization_id,
                plan_id: row.plan_id ?? null,
                plan_name: row.plan_name ?? null,
                subscription_id: row.subscription_id ?? null,
                subscription_status: row.subscription_status ?? null,
                trial_end: row.trial_end ?? null,
              },
              error: null,
            };
          }
        }
      } catch (_) {
        // fall through to local resolution
      }

      // Fallback: resolve effective plan from stripe_subscriptions + plans
      const { data: org, error: orgErr } = await supabase
        .from("organizations")
        .select("id, stripe_customer_id")
        .eq("id", organizationId)
        .single();
      if (orgErr || !org?.stripe_customer_id) {
        return { data: null, error: orgErr ? new Error(orgErr.message) : null };
      }

      const { data: subs, error: subsErr } = await supabase
        .from("stripe_subscriptions")
        .select("id,status,price_id,current_period_end,trial_end")
        .eq("customer_id", org.stripe_customer_id)
        .order("current_period_end", { ascending: false })
        .limit(10);
      if (subsErr) return { data: null, error: new Error(subsErr.message) };

      const priority = [
        "active",
        "trialing",
        "past_due",
        "incomplete",
        "paused",
        "canceled",
      ];
      const pick = (subs || []).sort((a: any, b: any) => {
        const ai = priority.indexOf(String(a.status || ""));
        const bi = priority.indexOf(String(b.status || ""));
        return ai - bi;
      })[0];

      if (!pick) {
        return {
          data: {
            organization_id: organizationId,
            plan_id: null,
            plan_name: null,
            subscription_id: null,
            subscription_status: null,
            trial_end: null,
          },
          error: null,
        };
      }

      let planId: string | null = null;
      let planName: string | null = null;
      if (pick.price_id) {
        const { data: plan, error: planErr } = await supabase
          .from("plans")
          .select("id,name,stripe_price_id")
          .eq("stripe_price_id", pick.price_id)
          .single();
        if (!planErr && plan) {
          planId = plan.id;
          planName = plan.name;
        }
      }

      return {
        data: {
          organization_id: organizationId,
          plan_id: planId,
          plan_name: planName,
          subscription_id: pick.id,
          subscription_status: pick.status,
          trial_end: pick.trial_end ?? null,
        },
        error: null,
      };
    } catch (e) {
      return {
        data: null,
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },

  async createCheckout(
    organizationId: string,
    planId: string,
  ): Promise<{
    url?: string;
    error?: Error;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-stripe-checkout",
        {
          body: { organizationId, planId },
        },
      );
      if (error) return { error: new Error(error.message) };
      if (!data?.url) return { error: new Error("No checkout URL returned") };
      return { url: data.url };
    } catch (e) {
      return { error: e as Error };
    }
  },
};
